import { getErrorMessage } from '@/api/errors'
import { workItemsApi } from '@/api/workItems'
import {
  AzureDevOpsIngestionWarning,
  BacklogFilters,
  BacklogHeader,
  BacklogSelectionBar,
  BacklogStatsCards,
  BacklogTable,
  BulkDeleteWorkItemsDialog,
  BulkStatusResultDialog,
  BulkUpdateStatusDialog,
  CreateWorkItemDialog,
  ImportAzureDevOpsDialog,
  PushToDevOpsDialog,
} from '@/components/projects/backlog'
import type { BulkStatusResultItem } from '@/components/projects/backlog'
import { TriggerWorkflowDialog } from '@/components/projects/workflows/TriggerWorkflowDialog'
import { TriggerWorkflowFromEntityDialog, type TriggerableDefinition } from '@/components/projects/workflows/TriggerWorkflowFromEntityDialog'
import { Pagination } from '@/components/ui'
import {
  useAuth,
  useBulkDeleteWorkItems,
  useBulkUpdateWorkItemStatus,
  useConnectors,
  useCsvExport,
  useDebounce,
  usePagination,
  useProjectTeamMembers,
  useWorkItemEpics,
  useWorkItems,
  useWorkItemStats,
} from '@/hooks'
import type { BulkDeleteWorkItemsResponse } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { BulkWorkItemStatusTarget, WorkItem, WorkItemFilters } from '@/types'
import { normalizeWorkItemStatus } from '@/types'
import { NO_EPIC_FILTER_VALUE, UNASSIGNED_FILTER_VALUE, WORK_ITEM_DEFAULT_SORT_DIRECTIONS, type WorkItemSortColumn } from '@/types/workItem'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

const FILTER_KEYS = ['search', 'epic', 'status', 'assignedTo', 'sort', 'sortBy'] as const

interface BulkStatusOutcome {
  targetStatus: string
  updated: BulkStatusResultItem[]
  failed: BulkStatusResultItem[]
}

type BulkStatusInput = Pick<WorkItem, 'id' | 'number' | 'title' | 'status'>

type BulkStatusOutcomeKind = 'ok' | 'partial' | 'error'

const toStatusTarget = (workItem: BulkStatusInput): BulkWorkItemStatusTarget => ({
  workItemId: workItem.id,
  expectedStatus: normalizeWorkItemStatus(workItem.status) ?? workItem.status,
})

export function BacklogPage() {
  const { projectId } = useParams()
  const { hasPermission } = useAuth(projectId)
  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()
  const [searchParams, setSearchParams] = useSearchParams()

  const storageKey = `backlog-filters-${projectId}`

  const search = searchParams.get('search') ?? ''
  const epicsKey = JSON.stringify(searchParams.getAll('epic'))
  const selectedEpics = useMemo<string[]>(() => JSON.parse(epicsKey), [epicsKey])
  const statusKey = searchParams.getAll('status').join('|')
  const statuses = useMemo(() => (statusKey ? statusKey.split('|') : []), [statusKey])
  const assignedToKey = searchParams.getAll('assignedTo').join(',')
  const assignedTo = useMemo(() => (assignedToKey ? assignedToKey.split(',') : []), [assignedToKey])
  const sortByParam = searchParams.get('sortBy')
  const sortBy: WorkItemSortColumn =
    sortByParam && sortByParam in WORK_ITEM_DEFAULT_SORT_DIRECTIONS ? (sortByParam as WorkItemSortColumn) : 'number'
  const sortDirParam = searchParams.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : WORK_ITEM_DEFAULT_SORT_DIRECTIONS[sortBy]

  const persistFilters = useCallback((params: URLSearchParams) => {
    const filterParams = new URLSearchParams()
    FILTER_KEYS.forEach(k => {
      params.getAll(k).forEach(v => {
        if (v) filterParams.append(k, v)
      })
    })
    sessionStorage.setItem(storageKey, filterParams.toString())
  }, [storageKey])

  const setFilter = useCallback(
    (key: string, value: string, isEmpty: (v: string) => boolean = v => !v || v === 'all') => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (isEmpty(value)) next.delete(key)
        else next.set(key, value)
        persistFilters(next)
        return next
      }, { replace: true })
    },
    [setSearchParams, persistFilters],
  )

  const setMultiFilter = useCallback(
    (key: string, values: string[]) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete(key)
        values.forEach(v => next.append(key, v))
        persistFilters(next)
        return next
      }, { replace: true })
    },
    [setSearchParams, persistFilters],
  )

  useLayoutEffect(() => {
    if (FILTER_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(storageKey)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      new Set(savedParams.keys()).forEach(key => {
        next.delete(key)
        savedParams.getAll(key).forEach(value => next.append(key, value))
      })
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams, storageKey])

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [bulkPushOpen, setBulkPushOpen] = useState(false)
  const [bulkStatusTarget, setBulkStatusTarget] = useState<string | null>(null)
  const [bulkStatusResult, setBulkStatusResult] = useState<BulkStatusOutcome | null>(null)
  const [bulkTriggerPickerOpen, setBulkTriggerPickerOpen] = useState(false)
  const [bulkTriggerDefinition, setBulkTriggerDefinition] = useState<TriggerableDefinition | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const debouncedSearch = useDebounce(search)

  const statsQuery = useWorkItemStats(projectId!)
  const connectorsQuery = useConnectors(projectId!)
  const stats = statsQuery.data

  const azureDevOpsConnected = useMemo(
    () => (connectorsQuery.data ?? []).some(c => c.type === 'AzureDevOps' && c.isEnabled),
    [connectorsQuery.data],
  )

  const externalIdContextLoading = statsQuery.isLoading || connectorsQuery.isLoading
  const externalIdContextResolved = statsQuery.isSuccess && connectorsQuery.isSuccess
  const externalIdContextFailed = statsQuery.isError || connectorsQuery.isError
  const externalIdColumnApplies =
    externalIdContextFailed || azureDevOpsConnected || stats?.hasSyncedWorkItems === true

  const [externalIdColumnLatched, setExternalIdColumnLatched] = useState(false)

  useEffect(() => {
    setExternalIdColumnLatched(false)
  }, [projectId])

  useEffect(() => {
    if (externalIdContextResolved && externalIdColumnApplies) setExternalIdColumnLatched(true)
  }, [externalIdContextResolved, externalIdColumnApplies])

  const showExternalIdColumn = externalIdColumnLatched || externalIdColumnApplies

  const effectiveSortBy: WorkItemSortColumn =
    sortBy === 'externalId' && !showExternalIdColumn ? 'number' : sortBy
  const effectiveSortDirection: 'asc' | 'desc' =
    effectiveSortBy === sortBy ? sortDirection : WORK_ITEM_DEFAULT_SORT_DIRECTIONS[effectiveSortBy]

  const apiFilters: WorkItemFilters = useMemo(() => {
    const assigneeIds = assignedTo.filter(v => v !== UNASSIGNED_FILTER_VALUE)
    const epicNames = selectedEpics.filter(v => v !== NO_EPIC_FILTER_VALUE)
    return {
      search: debouncedSearch || undefined,
      epics: epicNames.length > 0 ? epicNames : undefined,
      noEpic: selectedEpics.includes(NO_EPIC_FILTER_VALUE) || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      assignedTo: assigneeIds.length > 0 ? assigneeIds : undefined,
      unassigned: assignedTo.includes(UNASSIGNED_FILTER_VALUE) || undefined,
      sortBy: effectiveSortBy,
      sortDirection: effectiveSortDirection,
    }
  }, [debouncedSearch, selectedEpics, statuses, assignedTo, effectiveSortBy, effectiveSortDirection])

  const { data, isLoading } = useWorkItems(projectId!, paginationParams, apiFilters)
  const workItems = useMemo(() => data?.items ?? [], [data])
  const totalCount = data?.totalCount ?? 0
  const { data: epics = [] } = useWorkItemEpics(projectId!)
  const { data: teamMembers = [] } = useProjectTeamMembers(projectId)
  const assigneeOptions = useMemo(
    () => [
      { value: UNASSIGNED_FILTER_VALUE, label: 'Unassigned' },
      ...teamMembers.map(m => ({ value: m.userId, label: m.displayName || m.email })),
    ],
    [teamMembers],
  )
  const bulkDeleteWorkItems = useBulkDeleteWorkItems(projectId!)
  const bulkUpdateWorkItemStatus = useBulkUpdateWorkItemStatus(projectId!)

  useEffect(() => {
    resetPage()
    setSelectedIds(new Set())
  }, [debouncedSearch, epicsKey, statuses, assignedToKey, resetPage])

  useEffect(() => {
    setSelectedIds(new Set())
  }, [page, pageSize])

  const handleToggleRow = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSortChange = (column: WorkItemSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : WORK_ITEM_DEFAULT_SORT_DIRECTIONS[column]
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('sortBy', column)
      next.set('sort', nextDirection)
      persistFilters(next)
      return next
    }, { replace: true })
    resetPage()
  }

  const handleToggleAll = useCallback((ids: string[]) => {
    setSelectedIds(prev => {
      const allSelected = ids.length > 0 && ids.every(id => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      }
      const next = new Set(prev)
      for (const id of ids) next.add(id)
      return next
    })
  }, [])

  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => {
      if (prev) setSelectedIds(new Set())
      return !prev
    })
  }, [])

  const handleCancelSelection = useCallback(() => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }, [])

  const selectedWorkItems = useMemo(
    () => workItems.filter(w => selectedIds.has(w.id)),
    [workItems, selectedIds],
  )

  const selectedWorkItemTitles = useMemo(
    () => Object.fromEntries(selectedWorkItems.map(w => [w.id, w.title])),
    [selectedWorkItems],
  )

  const pushDisabledReason = !azureDevOpsConnected ? 'Connect Azure DevOps to enable push' : undefined
  const hasConflictingStatuses = useMemo(() => {
    const statuses = new Set(selectedWorkItems.map(w => w.status))
    return statuses.size > 1
  }, [selectedWorkItems])
  const triggerDisabledReason = hasConflictingStatuses ? 'Select work items with the same status.' : undefined

  const canChangeStatus = hasPermission(Permissions.Project.WorkItem.Update, projectId)
  const canComplete = hasPermission(Permissions.Project.WorkItem.Complete, projectId)

  const canDelete = hasPermission(Permissions.Project.WorkItem.Delete, projectId)
  const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId)
  const canPush = hasPermission(Permissions.Project.WorkItem.Update, projectId)

  const handleBulkDeleteConfirm = useCallback(async () => {
    const ids = Array.from(selectedIds)
    let result: BulkDeleteWorkItemsResponse | undefined
    try {
      result = await bulkDeleteWorkItems.mutateAsync(ids)
    } catch {
      toast.error('Failed to delete work items')
      setBulkDeleteOpen(false)
      return
    }

    setBulkDeleteOpen(false)

    const deletedCount = result.deletedIds.length
    if (deletedCount > 0) {
      toast.success(`${deletedCount} ${deletedCount === 1 ? 'work item' : 'work items'} deleted successfully`)
    }

    result.skippedIds.forEach((skippedId, index) => {
      const item = workItems.find(w => w.id === skippedId)
      const label = item?.number != null ? `Work item # ${String(item.number).padStart(3, '0')}` : 'A work item'
      setTimeout(() => {
        toast.error(`${label} could not be deleted`)
      }, index * 300)
    })

    handleCancelSelection()
  }, [selectedIds, bulkDeleteWorkItems, workItems, handleCancelSelection])

  const applyBulkStatus = useCallback(async (
    targetStatus: string,
    items: BulkStatusInput[],
    carryOver: BulkStatusResultItem[] = [],
    previouslyUpdated: BulkStatusResultItem[] = [],
  ): Promise<BulkStatusOutcomeKind> => {
    const snapshot = new Map(items.map(w => [w.id, { id: w.id, number: w.number, title: w.title }]))

    let result
    try {
      result = await bulkUpdateWorkItemStatus.mutateAsync({
        status: targetStatus,
        items: items.map(toStatusTarget),
      })
    } catch (error) {
      toast.error(
        extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update work item statuses',
      )
      return 'error'
    }

    const fallback = (id: string) => snapshot.get(id) ?? { id, number: null, title: 'Work item' }

    const updated = [
      ...previouslyUpdated,
      ...result.updatedIds.map(id => ({ ...fallback(id), currentStatus: targetStatus })),
    ]
    const failed = [
      ...carryOver,
      ...result.failed.map(failure => ({
        ...fallback(failure.workItemId),
        reason: failure.reason,
        currentStatus: failure.currentStatus,
        errorMessage: failure.errorMessage,
      })),
    ]

    const attemptSucceeded = result.failed.length === 0
    if (attemptSucceeded) {
      const count = result.updatedIds.length
      toast.success(`${count} ${count === 1 ? 'work item' : 'work items'} moved to ${targetStatus}`)
    }

    setBulkStatusResult(failed.length === 0 ? null : { targetStatus, updated, failed })

    return attemptSucceeded ? 'ok' : 'partial'
  }, [bulkUpdateWorkItemStatus])

  const handleBulkStatusConfirm = useCallback(async () => {
    const targetStatus = bulkStatusTarget
    const items = selectedWorkItems
    if (targetStatus === null || items.length === 0) return

    const outcome = await applyBulkStatus(targetStatus, items)
    setBulkStatusTarget(null)
    if (outcome !== 'error') handleCancelSelection()
  }, [bulkStatusTarget, selectedWorkItems, applyBulkStatus, handleCancelSelection])

  const handleBulkStatusRetry = useCallback(async () => {
    if (bulkStatusResult === null) return

    const retryable = bulkStatusResult.failed.filter(item => item.currentStatus !== null)
    const carryOver = bulkStatusResult.failed.filter(item => item.currentStatus === null)
    if (retryable.length === 0) return

    await applyBulkStatus(
      bulkStatusResult.targetStatus,
      retryable.map(item => ({ id: item.id, number: item.number, title: item.title, status: item.currentStatus! })),
      carryOver,
      bulkStatusResult.updated,
    )
  }, [bulkStatusResult, applyBulkStatus])

  const exportCsv = useCsvExport('work items')
  const handleExportCsv = () => exportCsv.mutate({
    fetchCsv: () => workItemsApi.exportCsv(projectId!, selectedWorkItems.map(w => w.id)),
    count: selectedWorkItems.length,
  })

  const handleExportAllCsv = () => exportCsv.mutate({
    fetchCsv: () => workItemsApi.exportAllCsv(projectId!, apiFilters),
    count: totalCount,
  })

  return (
    <div className="space-y-6">
      <BacklogHeader
        projectId={projectId!}
        onCreateClick={() => setCreateDialogOpen(true)}
        onImportClick={() => setImportDialogOpen(true)}
        onExportCsvClick={handleExportAllCsv}
        exportDisabled={totalCount === 0}
      />
      <AzureDevOpsIngestionWarning projectId={projectId} />
      <BacklogStatsCards totalWorkItems={stats?.totalWorkItems ?? 0} />

      <BacklogFilters
        search={search}
        onSearchChange={(v) => setFilter('search', v, s => !s)}
        selectedEpics={selectedEpics}
        onEpicsChange={(v) => setMultiFilter('epic', v)}
        epics={epics}
        statuses={statuses}
        onStatusesChange={(v) => setMultiFilter('status', v)}
        assigneeOptions={assigneeOptions}
        assignedTo={assignedTo}
        onAssignedToChange={(v) => setMultiFilter('assignedTo', v)}
        sortBy={effectiveSortBy}
        sortDirection={effectiveSortDirection}
        selectionMode={selectionMode}
        onToggleSelectionMode={handleToggleSelectionMode}
        onClearAll={() => {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            FILTER_KEYS.forEach(k => next.delete(k))
            persistFilters(next)
            return next
          }, { replace: true })
          resetPage()
        }}
      />

      {isLoading || externalIdContextLoading ? (
        <div className="text-muted-foreground" role="status" aria-live="polite" data-testid="backlog-loading">Loading backlog...</div>
      ) : (
        <div className="rounded-2xl border overflow-hidden" data-testid="backlog-table-container">
          <BacklogTable
            workItems={workItems}
            projectId={projectId!}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleRow={handleToggleRow}
            onToggleAll={handleToggleAll}
            azureDevOpsConnected={azureDevOpsConnected}
            showExternalIdColumn={showExternalIdColumn}
            onSortChange={handleSortChange}
            sortBy={effectiveSortBy}
            sortDirection={effectiveSortDirection}
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <BacklogSelectionBar
        selectedCount={selectedIds.size}
        onTrigger={canTrigger ? () => setBulkTriggerPickerOpen(true) : undefined}
        triggerDisabled={hasConflictingStatuses}
        triggerDisabledReason={triggerDisabledReason}
        onPush={canPush ? () => setBulkPushOpen(true) : undefined}
        onCancel={handleCancelSelection}
        pushDisabled={!azureDevOpsConnected || selectedIds.size === 0}
        pushDisabledReason={pushDisabledReason}
        onChangeStatus={canChangeStatus ? (status) => setBulkStatusTarget(status) : undefined}
        canCompleteWorkItems={canComplete}
        statusChangeDisabled={bulkUpdateWorkItemStatus.isPending}
        onDelete={canDelete ? () => setBulkDeleteOpen(true) : undefined}
        onExport={handleExportCsv}
      />

      <CreateWorkItemDialog
        projectId={projectId!}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      <ImportAzureDevOpsDialog
        projectId={projectId!}
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />

      <PushToDevOpsDialog
        projectId={projectId!}
        workItems={selectedWorkItems}
        open={bulkPushOpen}
        onOpenChange={setBulkPushOpen}
        onPushed={handleCancelSelection}
      />

      <BulkUpdateStatusDialog
        workItems={selectedWorkItems}
        targetStatus={bulkStatusTarget}
        open={bulkStatusTarget !== null}
        onOpenChange={(open) => { if (!open) setBulkStatusTarget(null) }}
        onConfirm={handleBulkStatusConfirm}
        isPending={bulkUpdateWorkItemStatus.isPending}
      />

      {bulkStatusResult !== null && (
        <BulkStatusResultDialog
          targetStatus={bulkStatusResult.targetStatus}
          updatedItems={bulkStatusResult.updated}
          failedItems={bulkStatusResult.failed}
          open
          onOpenChange={(open) => { if (!open) setBulkStatusResult(null) }}
          onRetry={handleBulkStatusRetry}
          isRetrying={bulkUpdateWorkItemStatus.isPending}
        />
      )}

      <BulkDeleteWorkItemsDialog
        workItems={selectedWorkItems}
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        onConfirm={handleBulkDeleteConfirm}
        isPending={bulkDeleteWorkItems.isPending}
      />

      <TriggerWorkflowFromEntityDialog
        projectId={projectId!}
        open={bulkTriggerPickerOpen}
        onOpenChange={setBulkTriggerPickerOpen}
        onSelect={(def) => setBulkTriggerDefinition(def)}
        linkedEntityType="WorkItem"
        linkedEntityId={selectedWorkItems[0]?.id ?? undefined}
      />

      <TriggerWorkflowDialog
        projectId={projectId!}
        definition={bulkTriggerDefinition}
        open={bulkTriggerDefinition !== null}
        onOpenChange={(open) => { if (!open) setBulkTriggerDefinition(null) }}
        extraInputValues={{ workItemId: '__bulk__' }}
        extraInputValuesPerEntity={(id) => ({ workItemId: id })}
        linkedEntityIds={Array.from(selectedIds)}
        linkedEntityType="WorkItem"
        entityTitles={selectedWorkItemTitles}
        onTriggerSuccess={handleCancelSelection}
      />
    </div>
  )
}
